import { useState, useMemo } from 'react';
import { TreeDataNode } from 'antd';

import {
  DarwinCard,
  DarwinFlex,
  DarwinTree,
  SearchBox,
} from 'components/darwin-ui';

import style from './TreeSection.module.css';

interface TreeSectionProps {
  treeData: TreeDataNode[];
  initialSelectedKeys?: React.Key[];
  onChange?: (selectedKeys: React.Key[]) => void;
  searchPlaceholder?: string;
  readonly?: boolean;
}

export const TreeSection = ({
  searchPlaceholder,
  treeData,
  initialSelectedKeys,
  onChange,
  readonly = false,
}: TreeSectionProps) => {
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>(
    initialSelectedKeys || []
  );
  const [searchValue, setSearchValue] = useState('');

  const handleCheck = (
    checkedKeys:
      | React.Key[]
      | { checked: React.Key[]; halfChecked: React.Key[] }
  ) => {
    const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    setSelectedKeys(keys);
    onChange?.(keys);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const filteredTreeData = useMemo(() => {
    if (!searchValue) return treeData;

    const filterTreeNode = (node: TreeDataNode): TreeDataNode | null => {
      const title = node.title?.toString().toLowerCase();
      const search = searchValue.toLowerCase();

      if (title && title.includes(search)) {
        // If the node's title matches, include it
        return node;
      } else if (node.children) {
        // Recursively filter the children
        const filteredChildren = node.children
          .map(child => filterTreeNode(child))
          .filter(child => child !== null) as TreeDataNode[];

        if (filteredChildren.length > 0) {
          // If any children match, include this node with filtered children
          return { ...node, children: filteredChildren };
        }
      }
      // Exclude the node if it doesn't match and has no matching children
      return null;
    };

    return treeData
      .map(node => filterTreeNode(node))
      .filter(node => node !== null) as TreeDataNode[];
  }, [treeData, searchValue]);

  return (
    <DarwinCard>
      <DarwinFlex
        vertical
        className={style.cardContainer}
        style={{ maxHeight: '500px', overflowY: 'auto' }}
      >
        <SearchBox placeholder={searchPlaceholder} onSearch={handleSearch} />
        <DarwinTree
          className={style.treeSelect}
          treeData={filteredTreeData}
          checkable
          onCheck={handleCheck}
          checkedKeys={selectedKeys}
          defaultExpandAll
          disabled={readonly}
        />
      </DarwinFlex>
    </DarwinCard>
  );
};
